// Importing necessary dependencies and components
import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { MapPin, Building2, Clock, Briefcase, Euro, ArrowLeft } from 'lucide-react';
import { LoadingSpinner } from '../components/LoadingSpinner'; // Spinner for loading state
import { formatDate, formatSalary } from '../lib/utils'; // Utility functions
import { getWhatsAppLink } from '../lib/utils/whatsapp'; // WhatsApp link generator
import type { Job } from '../types/job'; // Job type definition

/**
 * JobDetails Component
 * Displays job details and generates a WhatsApp link with the job title and URL.
 */
export function JobDetails() {
  const { id } = useParams<{ id: string }>(); // Retrieve job ID from URL
  const navigate = useNavigate(); // Hook for navigation
  const [jobData, setJobData] = useState<Job | null>(null); // State for job data
  const [loading, setLoading] = useState(true); // Loading state
  const [error, setError] = useState<string | null>(null); // Error state

  useEffect(() => {
    const fetchJob = async () => {
      try {
        setLoading(true);
        const response = await fetch(`https://x1lc-aku3-stjs.p7.xano.io/api:moW4brDB/job/${id}`);
        if (!response.ok) throw new Error('Failed to fetch job details.');
        const job = await response.json();
        setJobData(job);
      } catch {
        setError("Impossible de charger les détails de l'offre.");
      } finally {
        setLoading(false);
      }
    };

    if (id) fetchJob();
  }, [id]);

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-[50vh]">
        <LoadingSpinner />
      </div>
    );
  }

  if (error || !jobData) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-8">
        <div className="bg-red-50 text-red-700 p-4 rounded-lg">
          {error || "Offre d'emploi non trouvée."}
        </div>
      </div>
    );
  }

  const job = jobData;
  const jobUrl = `${window.location.origin}/jobs/${job.id}`; // URL complète du job

  // Message personnalisé pour le job
  const customMessage = `Je souhaite postuler à l'offre "${job.job_title}". Voici le lien : ${jobUrl}`;
  const whatsAppLink = getWhatsAppLink(customMessage);

  return (
    <div className="max-w-3xl mx-auto px-4 py-8">
      {/* Back Button */}
      <button
        onClick={() => navigate(-1)}
        className="mb-6 flex items-center text-gray-600 hover:text-gray-900"
      >
        <ArrowLeft className="h-5 w-5 mr-2" />
        Retour aux offres
      </button>

      {/* Job Details Card */}
      <div className="bg-white rounded-lg shadow-lg overflow-hidden">
        <div className="p-6">
          <div className="mb-6">
            <h1 className="text-3xl font-bold text-gray-900 mb-4">{job.job_title}</h1>
            <div className="flex items-center text-gray-500">
              <Clock className="h-5 w-5 mr-2" />
              <span>Publié le {formatDate(job.created_at)}</span>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
            <div className="space-y-4">
              <div className="flex items-center text-gray-700">
                <Building2 className="h-5 w-5 mr-3" />
                <div>
                  <p className="font-medium">{job.business}</p>
                  <p className="text-sm text-gray-500">{job.business_sector}</p>
                </div>
              </div>
              <div className="flex items-center text-gray-700">
                <MapPin className="h-5 w-5 mr-3" />
                <div>
                  <p className="font-medium">{job.city}</p>
                  <p className="text-sm text-gray-500">
                    {job.state} - {job.postal_code}
                  </p>
                  <p className="text-sm text-gray-500">{job.address}</p>
                </div>
              </div>
            </div>

            <div className="space-y-4">
              <div className="flex items-center text-gray-700">
                <Briefcase className="h-5 w-5 mr-3" />
                <div>
                  <p className="font-medium">{job.contract}</p>
                  <p className="text-sm text-gray-500">{job.required_experience}</p>
                </div>
              </div>
              {job.salary > 0 && (
                <div className="flex items-center text-gray-700">
                  <Euro className="h-5 w-5 mr-3" />
                  <div>
                    <p className="font-medium">{formatSalary(job.salary)}/an</p>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Job Description */}
          <div className="prose max-w-none mb-8">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">Description du poste</h2>
            <div className="text-gray-700 whitespace-pre-wrap">{job.description}</div>
          </div>

          {/* WhatsApp Application Button */}
          <div className="border-t pt-6">
            <a
              href={whatsAppLink}
              target="_blank"
              rel="noopener noreferrer"
              className="block w-full md:w-auto text-center bg-green-600 text-white px-6 py-3 rounded-lg hover:bg-green-700 transition-colors"
            >
              Postuler via WhatsApp
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}
